import { MTSidebarSwitcher } from '@/multi-territory/mt-components/mt-shared/MTSidebarSwitcher.js'
import { getEntityCountryDropdown } from '@/multi-territory/mt-services/mt-entity-api'
import { fetchNavigationModules } from '@/multi-territory/mt-services/mt-navigation'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { logoutUser, refreshToken } from '../../api/auth'
import { getUserRole } from '../../api/auth.js'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { sanitizeForLog } from '../../constants/index'
import { useFullScreen } from '../../contexts/FullScreenContext'
import { useSidebar } from '../../contexts/SidebarContext'
import { ROUTES } from '../../routes/routeConfig'
import { clearCountry, setCountry } from '../../store/slice/countrySlice'
import {
  resetEntityTanState,
  resetSelections,
} from '../../store/slice/entityTanSlice.js'
import { resetMTEntityTan } from '../../store/slice/mtEntityTanSlice'
import {
  clearNavigation,
  setCountryOptions,
  setNavModules,
} from '../../store/slice/navigationSlice'
import { clearUserRole, setUserRole } from '../../store/slice/userSlice.js'
import {
  clearSessionAuth,
  getAccessToken,
  getRefreshToken,
  getUsername,
  setAccessToken,
  setCountryDetails,
  setRefreshToken,
} from '../../utils/session'
import Footer from './Footer'
import Header from './Header.jsx'

const Layout = ({ children, hideFooter = false }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [user, setUser] = useState('')
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const { exitFullScreen } = useFullScreen()
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } =
    useSidebar()

  const userRole = useSelector(state => state.user.role)
  const countryCode = useSelector(state => state.country.countryCode)
  const { hasFetchedNav, hasFetchedCountryOptions } = useSelector(
    state => state.navigation
  )

  const handleClick = event => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  // Clear all session + Redux state and go to login without leaving the
  // current page in history (so a fresh login never lands on a stale page).
  const resetAndGoToLogin = () => {
    clearSessionAuth()
    dispatch(resetEntityTanState())
    dispatch(resetSelections())
    dispatch(clearUserRole())
    dispatch(clearNavigation())
    dispatch(clearCountry())
    dispatch(resetMTEntityTan())
    navigate(ROUTES.LOGIN, { replace: true })
  }

  const refreshAccessToken = async () => {
    try {
      const refreshTokenValue = getRefreshToken()
      if (refreshTokenValue) {
        const tokenData = await refreshToken()
        setAccessToken(tokenData.access_token, Number(tokenData.expires_in))
        setRefreshToken(tokenData.refresh_token)
      }
    } catch (error) {
      console.error('Error refreshing access token:')
      resetAndGoToLogin()
    }
  }

  // Fetch and store user role in Redux
  const fetchUserRole = async () => {
    try {
      const roleData = await getUserRole()
      if (roleData) {
        dispatch(setUserRole(roleData))
      }
    } catch (error) {
      console.error(
        'Error fetching user role:',
        sanitizeForLog(error?.message || 'Unknown error')
      )
      dispatch(clearUserRole())
    }
  }

  useEffect(() => {
    const checkTokenExpiration = () => {
      const authToken = getAccessToken()
      const refreshTokenValue = getRefreshToken()

      if (!authToken || !refreshTokenValue) {
        resetAndGoToLogin()
        return
      }

      const tokenExpTime = sessionStorage.getItem('tokenExpiresIn')
      const currentTime = Math.floor(Date.now() / 1000)

      if (currentTime >= tokenExpTime) {
        refreshAccessToken()
      }
    }

    const storedUsername = getUsername()
    if (storedUsername) setUser(storedUsername)

    // Initial token check
    checkTokenExpiration()

    // Fetch role after successful token validation
    fetchUserRole()

    const interval = setInterval(checkTokenExpiration, 60000)
    return () => clearInterval(interval)
  }, [navigate])

  // Fetch country dropdown options - only once per session
  useEffect(() => {
    if (hasFetchedCountryOptions) return

    getEntityCountryDropdown().then(response => {
      if (
        response.success &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        dispatch(setCountryOptions(response.data))
        // Select first country as default if current countryCode is not in list
        const isCurrentValid = response.data.some(
          opt => opt.country_code === countryCode
        )
        if (!isCurrentValid) {
          const first = response.data[0]
          const country = {
            countryName: first.country_name,
            countryCode: first.country_code,
          }
          dispatch(setCountry(country))
          setCountryDetails(country)
        }
      }
    })
  }, [hasFetchedCountryOptions])

  // Fetch navigation modules - skip for India (uses hardcoded sidebar), wait for country options first
  useEffect(() => {
    if (hasFetchedNav || !countryCode || !hasFetchedCountryOptions) return
    if (countryCode === GLOBAL_CONSTANTS.INDIA_COUNTRY_CODE) return

    fetchNavigationModules(countryCode)
      .then(result => {
        if (result.success && result.data?.data?.modules) {
          dispatch(setNavModules(result.data.data.modules))
        } else {
          // API returned no usable modules — mark as fetched so the sidebar
          // stops showing the skeleton and falls back to the config sidebar
          // (which still guarantees the Admin tab).
          dispatch(setNavModules([]))
        }
      })
      .catch(error => {
        console.error(
          'Error fetching navigation modules:',
          sanitizeForLog(error?.message || 'Unknown error')
        )
        // On error, still mark nav as fetched so the sidebar renders its
        // fallback instead of the skeleton forever.
        dispatch(setNavModules([]))
      })
  }, [hasFetchedNav, countryCode, hasFetchedCountryOptions])

  const handleLogout = async () => {
    try {
      await logoutUser()
    } catch (error) {
      console.log(error, 'error')
    } finally {
      // Always clear state even if API call fails
      resetAndGoToLogin()
    }
  }

  // Inactivity logout
  useEffect(() => {
    let inactivityTimer
    const resetTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer)
      inactivityTimer = setTimeout(
        () => handleLogout(),
        GLOBAL_CONSTANTS.INACTIVITY_LIMIT
      )
    }

    const events = [
      'mousemove',
      'mousedown',
      'click',
      'scroll',
      'keydown',
      'touchstart',
    ]
    events.forEach(e => window.addEventListener(e, resetTimer))
    resetTimer()

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer))
      if (inactivityTimer) clearTimeout(inactivityTimer)
    }
  }, [handleLogout])

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    )
  }

  return (
    <div className="h-screen flex overflow-hidden">
      <MTSidebarSwitcher
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
        exitFullScreen={exitFullScreen}
        userRole={userRole}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header
          setIsMobileOpen={setIsMobileOpen}
          user={user}
          handleClick={handleClick}
          anchorEl={anchorEl}
          open={open}
          handleClose={handleClose}
          handleLogout={handleLogout}
        />

        <main className="flex-1 border-b border-[var(--border-subtel)] overflow-auto px-[15px] bg-[#ffffff] hide-scrollbar">
          {children}
        </main>

        {!hideFooter && (
          <footer className=" flex-shrink-0 -mt-[2px]">
            <Footer />
          </footer>
        )}
      </div>
    </div>
  )
}

export default Layout
